import { DefaultError, useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { useInfiniteHandler } from 'hooks';
import { IInfinitePageParams, IInfinitePageResponse } from 'services/types';
import { apiClient } from 'utils';
import { clearSearchParams } from 'utils/global/object.utils';
import { getPlainText } from 'utils/global/plainText.utils';
import { getDefaultInfinitePageParam } from 'utils/service/defaultInfinitePageParam';
import { useResponseHandler } from 'utils/service/responseHandler';
import {
  IChat,
  IChatMessage,
  IChatMessagesSearchParams,
  ICreateMessage,
  ICreateThread,
  IReadMessage,
} from './types';
import { listQueryKeys } from 'constants/queryKey';

export const useGetThreads = (params?: IInfinitePageParams) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(params?.page, params?.itemCount);

  return useInfiniteQuery<IChat[], DefaultError>({
    queryKey: [listQueryKeys.thread, params],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: 'Chat',
        params: {
          ...clearSearchParams(params),
          itemCount,
          page: pageParam,
        },
      });

      const data = await responseHandler<IInfinitePageResponse<IChat>>(response, {
        groupName: `useGetChats - ${pageParam}`,
      });

      return infiniteHandler<IChat>(data);
    },
    initialPageParam: page,
    getNextPageParam: getNextPageParam,
  });
};

export const useGetMessages = (params: IChatMessagesSearchParams) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(params.page, params.itemCount);

  return useInfiniteQuery<IChatMessage[], DefaultError>({
    queryKey: ['messages-list', params],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: 'Chat/Messages',
        params: {
          ...JSON.parse(JSON.stringify(params)),
          itemCount,
          page: pageParam,
        },
      });

      const data = await responseHandler<IInfinitePageResponse<IChatMessage>>(response, {
        groupName: `useGetMessagesList - ${pageParam}`,
      });

      return infiniteHandler<IChatMessage>(data);
    },
    initialPageParam: page,
    getNextPageParam: getNextPageParam,
  });
};

export const useGetThread = (id: string) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IChat, DefaultError>({
    queryKey: ['chat', id],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Chat/Thread/${id}`,
      });

      return responseHandler(response, {
        groupName: `useGetChat`,
        onNotFound: () => {},
      });
    },
  });
};

export const usePostThread = () => {
  const { responseHandler } = useResponseHandler();
  return useMutation<IChatMessage, DefaultError, ICreateThread>({
    mutationFn: async (body) => {
      const formData = new FormData();

      formData.append('Subject', body.subject);
      formData.append('Message', getPlainText(body.message));

      let attachemntIndex = 0;

      for (const attachment of body.attachments) {
        attachemntIndex = attachemntIndex + 1;
        formData.append(`Attachments`, attachment);
      }

      const response = await apiClient({
        method: 'POST',
        url: 'Chat/CreateThread',
        body: formData,
        withContentType: false,
      });

      return responseHandler(response, {
        groupName: `usePostThread`,
        onNotFound: () => {},
      });
    },
  });
};

export const usePostMessage = () => {
  const { responseHandler } = useResponseHandler();
  return useMutation<IChatMessage, DefaultError, ICreateMessage>({
    mutationFn: async (body) => {
      const formData = new FormData();

      formData.append('ThreadId', body.threadId);
      formData.append('Message', body.message);

      for (const attachment of body.attachments) {
        formData.append(`Attachments`, attachment);
      }

      const response = await apiClient({
        method: 'POST',
        url: 'Chat/SendMessage',
        body: formData,
        withContentType: false,
      });

      return responseHandler(response, {
        groupName: `usePostMessage`,
        onNotFound: () => {},
      });
    },
  });
};

export const usePutReadMessage = () => {
  const { responseHandler } = useResponseHandler();
  return useMutation<IChatMessage, DefaultError, IReadMessage>({
    mutationFn: async (body) => {
      const response = await apiClient({
        method: 'PUT',
        url: 'Chat/ReadMessage',
        body: body,
      });

      return responseHandler(response, {
        groupName: `usePutReadMessage`,
        onNotFound: () => {},
      });
    },
  });
};

export const useGetUnreadThreadsCount = () => {
  const { responseHandler } = useResponseHandler();
  return useQuery<number, DefaultError>({
    queryKey: ['chat-unread-count'],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Chat/UnreadCount`,
      });

      return responseHandler(response, {
        groupName: `useGetUnreadThreadsCount`,
        onNotFound: () => {},
      });
    },
  });
};
